import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  FaTrash, FaSearch, FaBoxes, FaSync, FaFilter, FaCheckSquare, FaPlus,
} from 'react-icons/fa';
import {
  fetchBestsellers,
  removeBestseller,
} from '../services/apiService';
import { CardSkeleton, ButtonSpinner } from './LoadingSpinner';
import Pagination from './Pagination';
import BulkBestsellerModal from './BulkBestsellerModal';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getArticleImageUrl } from '../utils/imageUtils';
import PriceDisplay from './PriceDisplay';

// ── helpers ────────────────────────────────────────────────────────────────

// Extract a display article from any of the response shapes that have been
// emitted by this endpoint over time (unified, legacy, bare BestsellerProduct).
const extractArticle = (product) => {
  if (product?.articles?.length > 0) {
    const a = product.articles[0];
    return {
      pharmacode: a?.pharmacode ?? product.pharmacode,
      description: {
        description: a?.description?.description || 'No description available',
        longDescription: a?.description?.longDescription || 'No long description available',
      },
      hasPhoto: a?.hasPhoto || false,
      images: a?.images || {},
      price: a?.price ?? a?.prices ?? a?.priceInfo ?? null,
    };
  }
  const desc = product?.description;
  const descObj =
    typeof desc === 'string'
      ? { description: desc, longDescription: product?.longDescription || desc }
      : {
          description: desc?.description || 'No description available',
          longDescription: desc?.longDescription || product?.longDescription || 'No long description available',
        };
  return {
    pharmacode: product?.pharmacode,
    description: descObj,
    hasPhoto: product?.hasPhoto || false,
    images: product?.images || {},
    price: product?.price ?? product?.prices ?? product?.priceInfo ?? null,
  };
};

// ── filter helpers (operate on a bestseller "product" row) ──────────────────
// Numeric price for range filtering. The legacy endpoint stores either a
// resolved price object ({ currentPrice, basePrice }) or a bare Number
// snapshot (`prices`) — handle both.
const bestsellerPriceOf = (product) => {
  const p = extractArticle(product)?.price;
  if (p == null) return null;
  if (typeof p === 'number') return p;
  const v = p.currentPrice ?? p.basePrice ?? null;
  return v == null ? null : Number(v);
};

// `displayableOnStorefront` is stamped per-row by getAllProductsFromBestSeller:
// true when the bestseller actually surfaces in the customer carousel.
const bestsellerIsVisible = (product) => Boolean(product?.displayableOnStorefront);

const bestsellerHasImage = (product) => {
  const a = extractArticle(product);
  if (a?.hasPhoto) return true;
  const f = product?.images?.F;
  return Array.isArray(f) && f.length > 0;
};

// ── card components ────────────────────────────────────────────────────────

const IMAGE_DIMS = { width: 600, height: 600 };

const OptimizedImage = ({ src, alt, className }) => {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  return (
    <div className="relative w-full h-full">
      {isLoading && <div className="absolute inset-0 bg-gray-200 animate-pulse" />}
      <img
        src={src}
        alt={alt}
        width={IMAGE_DIMS.width}
        height={IMAGE_DIMS.height}
        loading="lazy"
        decoding="async"
        onLoad={() => setIsLoading(false)}
        onError={() => { setIsLoading(false); setHasError(true); }}
        className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
      />
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <span className="text-gray-500">{t('bestsellers.bestsellerCard.noImage')}</span>
        </div>
      )}
    </div>
  );
};

const BestsellerCard = ({ product, onDelete, onSelect, isSelected, isSelectionMode }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const article = extractArticle(product);
  const imageUrl = getArticleImageUrl(article, 'F');

  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`group relative border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-all duration-200 ${
        isSelected ? 'ring-2 ring-indigo-500 bg-indigo-50/30' : ''
      } ${isSelectionMode ? 'cursor-pointer hover:ring-2 hover:ring-indigo-300' : ''}`}
      onClick={(e) => {
        if (isSelectionMode) {
          e.preventDefault();
          e.stopPropagation();
          onSelect?.(article);
        } else if (!e.target.closest('button')) {
          navigate(`/products/${article.pharmacode}`);
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      {isSelectionMode && (
        <div className="absolute top-3 left-3 z-10">
          <div
            className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center ${
              isSelected ? 'bg-indigo-500 border-indigo-500 text-white shadow-sm' : 'bg-white/95 border-gray-300'
            }`}
          >
            {isSelected && <FaCheckSquare className="w-3 h-3" />}
          </div>
        </div>
      )}

      <div className="aspect-[4/3] w-full relative">
        <div className="absolute inset-0 p-3">
          {imageUrl ? (
            <OptimizedImage src={imageUrl} alt={article.description?.longDescription || 'Product Image'} className="w-full h-full object-contain" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
              <span>{t('bestsellers.bestsellerCard.noImage')}</span>
            </div>
          )}
        </div>
      </div>

      <div className="p-3 flex flex-col min-h-[120px]">
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-500 line-clamp-2">
            {t('bestsellers.bestsellerCard.pharmacode')}: {article.pharmacode}
          </h3>
          <p className="mt-1 text-sm italic text-gray-900 font-medium line-clamp-2">
            {article.description?.description || t('bestsellers.bestsellerCard.noDescription')}
          </p>
          <p className="text-sm text-gray-900 font-medium mt-1 line-clamp-2">
            {article.description?.longDescription || t('bestsellers.bestsellerCard.noLongDescription')}
          </p>
        </div>

        <div className="mt-2 pt-2 border-t border-gray-100">
          <PriceDisplay article={article} fallbackKey="bestsellers.bestsellerCard.noPrice" />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 mt-2">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete(article);
            }}
            className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm"
          >
            <FaTrash /> {t('bestsellers.bestsellerCard.remove')}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ── main component ─────────────────────────────────────────────────────────

const BestsellerManager = () => {
  const { t } = useLanguage();

  // ── list state ───────────────────────────────────────────────────────────
  const [bestsellers, setBestsellers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [totalBestsellers, setTotalBestsellers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [useUnifiedEndpoint, setUseUnifiedEndpoint] = useState(false);

  // ── search + filters (client-side, across the full curated set) ─────────
  const [searchTerm, setSearchTerm] = useState('');
  // The bestseller list is curated and bounded, so we load it all once and
  // filter/paginate in memory — the filters then apply to every bestseller,
  // not just the visible page, with no backend changes required.
  const [showFilters, setShowFilters] = useState(false);
  const [visibilityFilter, setVisibilityFilter] = useState('all'); // all | visible | hidden
  const [imageFilter, setImageFilter] = useState('all');           // all | with | without
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const PAGE_SIZE = 20;

  // ── bulk-add modal ──────────────────────────────────────────────────────
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);

  // ── selection mode ──────────────────────────────────────────────────────
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedBestsellers, setSelectedBestsellers] = useState([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const showTransientSuccess = useCallback((msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 5000);
  }, []);

  // Load the ENTIRE bestseller set (paging through the legacy endpoint) so the
  // search + filter panel can operate over everything and paginate client-side.
  // The `page` argument is accepted but ignored — callers still pass
  // `currentPage`, which no longer triggers a network fetch.
  const fetchBestsellersData = useCallback(async () => {
    const FETCH_LIMIT = 200;
    const MAX_PAGES = 50; // safety guard: 50 * 200 = 10k rows
    try {
      setIsLoading(true);
      setError(null);

      // Use the legacy `/getAllProductsFromBestSeller` endpoint directly.
      // The unified `/admin/products/unified?bestseller=true` endpoint was
      // intermittently returning 400 on prod (ES query shape mismatch) and
      // the legacy endpoint now correctly resolves article-level
      // pharmacodes via the nested path, so the unified detour added
      // latency and console noise for no benefit.
      const acc = [];
      let p = 1;
      let pages = 1;
      do {
        const legacy = await fetchBestsellers(p, FETCH_LIMIT);
        if (!legacy?.success) break;
        acc.push(...(legacy.data?.products || []));
        pages = legacy.data?.pages || 1;
        p += 1;
      } while (p <= pages && p <= MAX_PAGES);

      setBestsellers(acc);
      setTotalBestsellers(acc.length);
      setUseUnifiedEndpoint(false);
    } catch (e) {
      console.error('[BestsellerManager] fetch error:', e);
      setError(t('bestsellers.errors.fetchBestsellers'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchBestsellersData(1);
  }, [fetchBestsellersData]);

  // ── handlers ────────────────────────────────────────────────────────────

  const handlePageChange = (page) => {
    // Client-side pagination over the in-memory filtered set — no refetch.
    setCurrentPage(page);
  };

  const handleSelectBestseller = (bestseller) => {
    setSelectedBestsellers((prev) => {
      const already = prev.some((b) => b.pharmacode === bestseller.pharmacode);
      return already
        ? prev.filter((b) => b.pharmacode !== bestseller.pharmacode)
        : [...prev, bestseller];
    });
  };

  const handleExitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedBestsellers([]);
  };

  const handleBulkDelete = async () => {
    if (selectedBestsellers.length === 0) {
      setError(t('bestsellers.errors.selectOne'));
      return;
    }
    if (!deleteReason) {
      setError(t('bestsellers.errors.provideReason'));
      return;
    }
    try {
      setIsBulkDeleting(true);
      setError(null);
      for (const b of selectedBestsellers) {
        await removeBestseller(b.pharmacode);
      }
      await fetchBestsellersData(currentPage);
      setSelectedBestsellers([]);
      setIsBulkDeleteModalOpen(false);
      setDeleteReason('');
      setIsSelectionMode(false);
      showTransientSuccess(t('bestsellers.success.bulkRemove', { count: selectedBestsellers.length }));
    } catch (e) {
      console.error('[BestsellerManager] bulk delete error:', e);
      setError(e.response?.data?.message || t('bestsellers.errors.removeBestsellers'));
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleDelete = async (article) => {
    if (!window.confirm(t('bestsellers.success.confirmRemove'))) return;
    try {
      await removeBestseller(article.pharmacode);
      await fetchBestsellersData(currentPage);
      setError(null);
    } catch (e) {
      console.error('[BestsellerManager] delete error:', e);
      setError(e.response?.data?.message || t('bestsellers.errors.deleteBestseller'));
    }
  };

  // ── derived: search + filters applied across the full loaded set ────────

  const searchedBestsellers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return bestsellers;
    return bestsellers.filter((product) => {
      const a = extractArticle(product);
      return (
        (a.description?.longDescription || '').toLowerCase().includes(q) ||
        (a.description?.description || '').toLowerCase().includes(q) ||
        String(a.pharmacode || '').includes(searchTerm.trim())
      );
    });
  }, [bestsellers, searchTerm]);

  const filteredBestsellers = useMemo(() => {
    const min = priceMin !== '' ? Number(priceMin) : null;
    const max = priceMax !== '' ? Number(priceMax) : null;
    return searchedBestsellers.filter((product) => {
      if (visibilityFilter === 'visible' && !bestsellerIsVisible(product)) return false;
      if (visibilityFilter === 'hidden' && bestsellerIsVisible(product)) return false;
      if (imageFilter === 'with' && !bestsellerHasImage(product)) return false;
      if (imageFilter === 'without' && bestsellerHasImage(product)) return false;
      if (min != null || max != null) {
        const price = bestsellerPriceOf(product);
        if (price == null || Number.isNaN(price)) return false;
        if (min != null && price < min) return false;
        if (max != null && price > max) return false;
      }
      return true;
    });
  }, [searchedBestsellers, visibilityFilter, imageFilter, priceMin, priceMax]);

  const pageCount = Math.max(Math.ceil(filteredBestsellers.length / PAGE_SIZE), 1);
  const pageItems = filteredBestsellers.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const activeFilterCount =
    (visibilityFilter !== 'all' ? 1 : 0) +
    (imageFilter !== 'all' ? 1 : 0) +
    (priceMin !== '' || priceMax !== '' ? 1 : 0);

  const clearAllFilters = () => {
    setVisibilityFilter('all');
    setImageFilter('all');
    setPriceMin('');
    setPriceMax('');
  };

  // Reset to page 1 whenever the result set changes shape.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, visibilityFilter, imageFilter, priceMin, priceMax]);

  // Clamp the page if the filtered set shrank below the current page.
  useEffect(() => {
    if (currentPage > pageCount) setCurrentPage(pageCount);
  }, [pageCount, currentPage]);

  // ── render ──────────────────────────────────────────────────────────────

  return (
    <div className="bg-white">
      <div className="mx-auto max-w-[95%] px-2 sm:px-4 lg:px-6">
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="pb-8 pt-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div className="flex items-center gap-4 mb-4 lg:mb-0">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                <FaBoxes className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                  {t('bestsellers.title')}
                  {isSelectionMode && (
                    <span className="ml-3 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg text-lg font-medium text-indigo-700">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse" />
                      {t('bestsellers.selectionMode')}
                    </span>
                  )}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {isSelectionMode ? t('bestsellers.subtitleSelectionMode') : t('bestsellers.subtitle')}
                  {useUnifiedEndpoint && (
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                      {t('bestsellers.unifiedApi')}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowBulkAddModal(true)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2.5 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
              >
                <FaPlus className="text-sm" />
                {t('bestsellers.addNewBestseller')}
              </button>
              <button
                onClick={() => fetchBestsellersData(currentPage)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2.5 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FaSync className="text-sm" />
                {t('bestsellers.refresh')}
              </button>
            </div>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">{t('bestsellers.stats.totalBestsellers')}</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {isLoading ? <div className="animate-pulse bg-blue-200 h-8 w-16 rounded" /> : totalBestsellers.toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                  <FaBoxes className="text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">{t('bestsellers.stats.currentPage')}</p>
                  <p className="text-2xl font-bold text-green-900">{currentPage} of {pageCount}</p>
                </div>
                <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
                  <FaFilter className="text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">{t('bestsellers.stats.showing')}</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {filteredBestsellers.length} {t('bestsellers.stats.products')}
                  </p>
                </div>
                <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                  <FaSearch className="text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Search + Filters toggle */}
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('bestsellers.search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
              />
            </div>
            <button
              onClick={() => setShowFilters((open) => !open)}
              className={`inline-flex items-center justify-center gap-2.5 px-6 py-2.5 rounded-lg font-medium transition-all duration-200 shadow-sm flex-shrink-0 ${
                showFilters || activeFilterCount > 0
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
              }`}
            >
              <FaFilter className={showFilters || activeFilterCount > 0 ? 'text-white' : 'text-indigo-500'} />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-white/25 text-xs font-semibold">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Storefront visibility */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Storefront</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'all', label: 'All' },
                      { value: 'visible', label: 'Visible' },
                      { value: 'hidden', label: 'Hidden' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setVisibilityFilter(opt.value)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          visibilityFilter === opt.value
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Image presence */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Image</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'all', label: 'All' },
                      { value: 'with', label: 'With image' },
                      { value: 'without', label: 'No image' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setImageFilter(opt.value)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          imageFilter === opt.value
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price range */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Price (CHF)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Min"
                      value={priceMin}
                      onChange={(e) => setPriceMin(e.target.value)}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <span className="text-gray-400">–</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Max"
                      value={priceMax}
                      onChange={(e) => setPriceMax(e.target.value)}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {activeFilterCount > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {filteredBestsellers.length} of {bestsellers.length} bestsellers match
                  </span>
                  <button
                    onClick={clearAllFilters}
                    className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                  >
                    <FaSync className="text-xs" /> Clear filters
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Selection-mode controls */}
          {isSelectionMode && (
            <div className="mt-4 bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedBestsellers.length === pageItems.length && pageItems.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedBestsellers(pageItems.map(extractArticle));
                        else setSelectedBestsellers([]);
                      }}
                      className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {t('bestsellers.selection.selectAll')} ({pageItems.length})
                    </span>
                  </div>
                  {selectedBestsellers.length > 0 && (
                    <div className="flex items-center gap-2 px-2 py-1 bg-indigo-50 border border-indigo-200 rounded-md">
                      <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                      <span className="text-sm font-medium text-indigo-700">
                        {selectedBestsellers.length} {t('bestsellers.selection.selected')}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {selectedBestsellers.length > 0 && (
                    <button
                      onClick={() => { setDeleteReason(''); setIsBulkDeleteModalOpen(true); }}
                      disabled={isBulkDeleting}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                        isBulkDeleting
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                      }`}
                    >
                      {isBulkDeleting ? (
                        <>
                          <ButtonSpinner size="sm" />
                          {t('bestsellers.selection.removing')}
                        </>
                      ) : (
                        <>
                          <FaTrash className="text-xs" />
                          {t('bestsellers.selection.remove')} ({selectedBestsellers.length})
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={handleExitSelectionMode}
                    className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors duration-200"
                  >
                    {t('bestsellers.selection.cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Banners */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </div>
        )}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
            <FaCheckSquare className="h-5 w-5 text-green-600" />
            {successMessage}
          </div>
        )}

        {/* List */}
        <section aria-labelledby="bestsellers-heading" className="pb-24 pt-6">
          {filteredBestsellers.length > 0 && !isSelectionMode && (
            <div className="mb-6 flex items-center justify-end">
              <button
                onClick={() => setIsSelectionMode(true)}
                className="inline-flex items-center gap-2.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-5 py-2.5 rounded-xl hover:from-purple-600 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl font-medium text-sm group"
              >
                <div className="flex items-center justify-center w-4 h-4 bg-white/20 rounded-md group-hover:bg-white/30 transition-colors duration-200">
                  <FaCheckSquare className="text-xs" />
                </div>
                <span>{t('bestsellers.selection.selectBestsellers')}</span>
                <div className="w-1.5 h-1.5 bg-white/60 rounded-full group-hover:bg-white/80 transition-colors duration-200" />
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {isLoading ? (
              <CardSkeleton count={8} />
            ) : pageItems.length > 0 ? (
              pageItems.map((product) => {
                const article = extractArticle(product);
                const isSelected = selectedBestsellers.some((s) => s.pharmacode === article.pharmacode);
                return (
                  <BestsellerCard
                    key={product._id || article.pharmacode}
                    product={product}
                    onDelete={handleDelete}
                    onSelect={handleSelectBestseller}
                    isSelected={isSelected}
                    isSelectionMode={isSelectionMode}
                  />
                );
              })
            ) : (
              <div className="text-center col-span-full">
                <h3 className="text-lg font-medium text-gray-900">{t('bestsellers.empty.title')}</h3>
                <p className="text-gray-500">
                  {searchTerm ? t('bestsellers.empty.withSearch') : t('bestsellers.empty.noSearch')}
                </p>
              </div>
            )}
          </div>

          {pageCount > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={pageCount}
              onPageChange={handlePageChange}
              totalProducts={filteredBestsellers.length}
              isLoading={isLoading}
              showProductInfo
            />
          )}
        </section>
      </div>

      {/* Bulk-add modal — same search UX as the discount manager */}
      <BulkBestsellerModal
        isOpen={showBulkAddModal}
        onClose={() => setShowBulkAddModal(false)}
        onSuccess={(response) => {
          // Refresh so the new bestsellers show up; leave the modal open
          // so the admin can see the success/breakdown summary before
          // closing manually.
          fetchBestsellersData(currentPage);
          const successCount = response?.data?.summary?.successCount ?? 0;
          if (successCount > 0) {
            showTransientSuccess(
              t('bestsellers.success.bulkAdd', { count: successCount }) ||
              `Added ${successCount} bestseller${successCount === 1 ? '' : 's'}.`
            );
          }
        }}
      />

      {/* Bulk-delete confirmation modal — preserved from previous version */}
      {isBulkDeleteModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setIsBulkDeleteModalOpen(false)}
          />
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative bg-white rounded-lg max-w-lg w-full z-50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h3 className="text-lg font-medium mb-4">{t('bestsellers.modals.bulkDelete.title')}</h3>
                <p className="text-sm text-gray-700 mb-4">
                  {t('bestsellers.modals.bulkDelete.description', { count: selectedBestsellers.length })}
                </p>
                <textarea
                  placeholder={t('bestsellers.modals.bulkDelete.placeholder')}
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="w-full border rounded-md p-2 mb-4"
                  rows="3"
                />
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => setIsBulkDeleteModalOpen(false)}
                    className="px-4 py-2 border rounded-md"
                  >
                    {t('bestsellers.modals.bulkDelete.cancel')}
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={isBulkDeleting}
                    className={`px-4 py-2 rounded-md transition-all duration-200 ${
                      isBulkDeleting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
                    } text-white flex items-center gap-2`}
                  >
                    {isBulkDeleting ? (
                      <>
                        <ButtonSpinner size="sm" />
                        {t('bestsellers.modals.bulkDelete.removing')}
                      </>
                    ) : (
                      t('bestsellers.modals.bulkDelete.remove')
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BestsellerManager;
